import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../BrandUI";
import iconMail from "../../../../assets/brand/icon-mail.svg";
import backChevron from "../../../../assets/brand/onboarding/back-chevron.svg";
import closeIcon from "../../../../assets/brand/nav/close-18.svg";
import wordmark60 from "../../../../assets/brand/nav/wordmark-60.svg";
import navHome from "../../../../assets/brand/nav/nav-home.svg";
import navProfile from "../../../../assets/brand/nav/nav-profile.svg";
import navAtlas from "../../../../assets/brand/nav/nav-atlas.svg";
import navDeals from "../../../../assets/brand/nav/nav-deals.svg";
import navCircle from "../../../../assets/brand/nav/nav-circle.svg";
import navTeam from "../../../../assets/brand/nav/nav-team.svg";
import navCacheShare from "../../../../assets/brand/nav/nav-cacheshare.svg";

// Phone shell chrome for Athlete Mode, Coach Mode and the sign-up frame
// (Figma ATHLETE MODE V2 / COACH MODE V2 mobile). Presentational only: the
// shells decide which chrome a route gets and pass the data in. Only rendered
// below 640px, where the page is never zoomed, so sticky and fixed behave.

const cx = (...parts) => parts.filter(Boolean).join(" ");

// The bottom nav glyphs (Figma 1948:845..872, I2406:1089;1948:1142 / 1149).
export const NAV_ICONS = {
  home: navHome,
  profile: navProfile,
  atlas: navAtlas,
  deals: navDeals,
  circle: navCircle,
  team: navTeam,
  cacheshare: navCacheShare,
};

// Paints an SVG file through a CSS mask so its colour comes from
// `currentColor`: one unmodified file serves the active (orange) and the
// inactive (muted) state, as NavIcon does in the edit hub.
export const MaskIcon = ({ src, size = 24, className = "" }) => {
  const url = `url("${src}")`;
  const box = `${size}px ${size}px`;
  return (
    <span
      aria-hidden="true"
      className={cx("inline-block shrink-0 bg-current", className)}
      style={{
        width: size,
        height: size,
        WebkitMaskImage: url,
        maskImage: url,
        WebkitMaskSize: box,
        maskSize: box,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
  );
};

// Tab pages' top bar (AppShell · Mobile 2000:306, coach 2406:746): wordmark
// home link, the mail link with its count, then the shell's avatar menu.
// 55px tall with its rule. `mail` is { to, label, count } or null.
export const MobileTopBar = ({ homeTo, homeLabel, mail, menu }) => (
  <header className="sticky top-0 z-30 bg-white">
    <div className="flex items-center justify-between pl-5 pr-4 pt-[10px] pb-[14px]">
      <Link to={homeTo} aria-label={homeLabel} className="flex items-center">
        <img src={wordmark60} alt="CACHE" className="h-[12.283px] w-[60px]" />
      </Link>
      <div className="flex items-center gap-[14px]">
        {mail && (
          <Link to={mail.to} aria-label={mail.label} className="flex items-center gap-[5px]">
            <img src={iconMail} alt="" className="h-5 w-5" />
            {mail.count > 0 && <Badge tone="brand">{mail.count}</Badge>}
          </Link>
        )}
        {menu}
      </div>
    </div>
    <div className="h-px bg-bm-border" />
  </header>
);

const activeTabKey = (tabs, pathname) => {
  const path = pathname || "";
  const hit = tabs.find((tab) => (tab.match || [tab.to]).some((m) => path.startsWith(m)));
  return hit ? hit.key : null;
};

// Fixed bottom tab bar (BottomNav / Athlete 2000:333, BottomNav / Coach
// 2406:1089). Tabs are { key, to, label, icon, match: string[], badge? },
// where `badge` names a count in `badges`. The first tab whose `match`
// prefixes the pathname is current. A link's accessible name is its label,
// then the count ("Atlas 3", as on the desktop tabs).
export const MobileBottomNav = ({ label, tabs = [], pathname, badges = {} }) => {
  const activeKey = activeTabKey(tabs, pathname);
  return (
    <nav aria-label={label} className="fixed inset-x-0 bottom-0 z-30 border-t border-bm-border bg-white">
      <ul className="flex justify-center pt-[10px] pb-[calc(8px+env(safe-area-inset-bottom))]">
        {tabs.map((tab) => {
          const active = tab.key === activeKey;
          const count = tab.badge ? Number(badges?.[tab.badge]) || 0 : 0;
          return (
            <li key={tab.key} className="flex-1">
              <Link
                to={tab.to}
                aria-current={active ? "page" : undefined}
                className={cx(
                  "flex flex-col items-center gap-[5px] pt-1 pb-[2px]",
                  active ? "text-bm-orange" : "text-bm-muted",
                )}
              >
                <span className="relative h-6 w-6">
                  <MaskIcon src={tab.icon} />
                  {count > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute left-3 top-0 flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-bm-orange px-[6px] bm-label-xs text-white"
                    >
                      {count}
                    </span>
                  )}
                </span>
                <span className="bm-label-xs whitespace-nowrap">{tab.label}</span>
                {count > 0 && <span className="sr-only"> {count}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

const LEADING = {
  back: { icon: backChevron, iconClass: "h-5 w-5", label: "Back" },
  close: { icon: closeIcon, iconClass: "h-[18px] w-[18px]", label: "Close" },
};

const StackAction = ({ action }) => {
  const { label, onClick, to, href, ariaLabel, target, rel, disabled } = action;
  const className =
    "inline-flex min-h-[40px] min-w-[40px] items-center justify-end justify-self-end whitespace-nowrap bm-label-l text-bm-orange disabled:cursor-not-allowed disabled:opacity-50";
  if (to) {
    return (
      <Link to={to} aria-label={ariaLabel} className={className}>
        {label}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
        aria-label={ariaLabel}
        className={className}
      >
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel} disabled={disabled} className={className}>
      {label}
    </button>
  );
};

// Stack screens' header (MobileShell 1767:735): a back chevron or a close X
// on the left, the screen title, "Step N of M" or the wordmark in the
// centre, one orange text action on the right, and the step bars under it
// when `progress` ({ step, total }) is set. No rule underneath.
// `leading` is "back", "close" or null; `action` is
// { label, onClick | to | href, ariaLabel }.
export const MobileStackHeader = ({
  title,
  progress,
  leading = null,
  onLeading,
  leadingLabel,
  action,
  logo = false,
  sticky = true,
}) => {
  const lead = leading ? LEADING[leading] : null;
  const total = progress ? Math.max(0, Math.floor(Number(progress.total) || 0)) : 0;
  const step = progress ? Number(progress.step) || 0 : 0;
  let centre = null;
  if (progress) centre = `Step ${progress.step} of ${progress.total}`;
  else if (title) centre = title;
  else if (logo) centre = <img src={wordmark60} alt="CACHE" className="inline-block h-[12.283px] w-[60px] align-middle" />;

  // The side columns never go below their content (the 40px slots, or a
  // long action such as "Save and log out"), so a long title truncates
  // instead of running under the action; with room to spare both sides
  // share it equally and the title stays centred.
  return (
    <header className={sticky ? "sticky top-0 z-30 bg-white" : "bg-white"}>
      <div className="grid grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1fr)] items-center gap-x-2 px-4 pt-[6px] pb-[10px]">
        {lead ? (
          <button
            type="button"
            onClick={onLeading}
            aria-label={leadingLabel || lead.label}
            className="flex h-10 w-10 items-center justify-center rounded-[8px] hover:bg-bm-subtle"
          >
            <img src={lead.icon} alt="" className={lead.iconClass} />
          </button>
        ) : (
          <span className="h-10 w-10" aria-hidden="true" />
        )}
        <div className="min-w-0 truncate text-center bm-label-l text-bm-muted">{centre}</div>
        {action ? <StackAction action={action} /> : <span className="h-10 w-10 justify-self-end" aria-hidden="true" />}
      </div>
      {progress && total > 0 && (
        <div className="flex gap-[6px] px-4 pb-[10px]" aria-hidden="true">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={cx("h-1 flex-1 rounded-[4px]", i < step ? "bg-bm-orange" : "bg-bm-mutedbg")} />
          ))}
        </div>
      )}
    </header>
  );
};

// Horizontal tab strip (TabBar · Mobile 1976:981, TabItem 1969:761). Tabs
// are { value, label, id?, controls? }. Arrow keys, Home and End move
// between tabs (and select them); the active tab scrolls into view when the
// value changes. `sticky` pins it under the 55px MobileTopBar.
export const MobileTabBar = ({ tabs = [], value, onChange, ariaLabel, sticky = false, className = "" }) => {
  const refs = useRef({});

  useEffect(() => {
    const el = refs.current[value];
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [value]);

  const hasActive = tabs.some((tab) => tab.value === value);

  const move = (index) => {
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    refs.current[tab.value]?.focus();
    if (tab.value !== value) onChange?.(tab.value);
  };

  const onKeyDown = (e, index) => {
    if (e.key === "ArrowRight") move(index + 1);
    else if (e.key === "ArrowLeft") move(index - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(tabs.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(
        "flex items-end gap-5 overflow-x-auto px-6 border-b border-bm-mutedbg bg-white bm-no-scrollbar",
        sticky && "sticky top-[55px] z-20",
        className,
      )}
    >
      {tabs.map((tab, i) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(el) => {
              refs.current[tab.value] = el;
            }}
            type="button"
            role="tab"
            id={tab.id}
            aria-controls={tab.controls}
            aria-selected={active}
            tabIndex={active || (!hasActive && i === 0) ? 0 : -1}
            onClick={() => onChange?.(tab.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className="flex shrink-0 flex-col gap-[10px] pt-3"
          >
            <span className={cx("bm-label-l whitespace-nowrap", active ? "text-bm-orange" : "text-bm-muted")}>{tab.label}</span>
            <span aria-hidden="true" className={cx("h-[2px] w-full bg-bm-orange", !active && "opacity-0")} />
          </button>
        );
      })}
    </div>
  );
};
